import { Injectable } from '@angular/core';
import {
    IsActiveMatchOptions,
    NavigationEnd,
    Router,
    UrlTree,
} from '@angular/router';
import { filter } from 'rxjs';
import { DashboardRoute } from 'src/app/modules/app-routing.module';
import { AuthService, GuardData } from '../auth.service';

export interface NavbarData {
    navbarGroup?: string;
    disabled?: boolean;
}

export interface NavbarGroup {
    groupName: string;
    items: DashboardRoute[];
    selectedTitle?: string;
}

@Injectable({
    providedIn: 'root',
})
export class NavbarService {
    readonly groupOrder = ['Campaigns', 'Tech', 'Dev'];
    show: boolean = false;
    readonly dashboards: DashboardRoute[] = [];
    ungrouped: DashboardRoute[] = [];
    grouped: NavbarGroup[] = [];
    showDevDashboards: boolean = false;

    constructor(private auth: AuthService, private router: Router) {
        this.show = this.auth.getUser() != null;
        this.auth.listenState().subscribe(async (state) => {
            this.show = (await this.auth.getUser()) != null;

            this.initDashboards();
            this.setNavbarSubmenuTitle();
        });

        this.router.events
            .pipe(filter((e) => e instanceof NavigationEnd))
            .subscribe(() => this.setNavbarSubmenuTitle());
    }

    /**
     * Set authorized dashboards for the user.
     *
     * This method only sets dashboards to which the current user has permissions.
     */
    public async initDashboards() {
        // @ts-ignore: readonly property assignment for initialization only
        this.dashboards = (await this.getAvailableDashboards()).filter((d) => {
            const data = d.data as NavbarData | undefined;
            return !(
                data?.disabled ||
                (!this.showDevDashboards && data?.navbarGroup === 'Dev')
            );
        });
        this.ungrouped = this.dashboards.filter(
            (d) => !(d.data as NavbarData)?.navbarGroup
        );
        this.grouped = NavbarService.getGroupedDashboards(
            this.dashboards,
            this.groupOrder
        );
    }

    /**
     * Returns the list of accessible dashboards.
     *
     * This method searches for the “dashboards” route and retrieves all child
     * elements available to the current user along with the corresponding project ID.
     * @returns Promise<DashboardRoute[]>
     */
    private async getAvailableDashboards(): Promise<DashboardRoute[]> {
        const dashboardsRoute = this.router.config.find(
            (r) => r.path === 'dashboards'
        );
        if (!dashboardsRoute?.children) {
            return [];
        }

        const result: DashboardRoute[] = [];

        for (const projectRoute of dashboardsRoute.children) {
            if (!projectRoute.children) continue;

            const projectRouteData = projectRoute.data as GuardData;
            const canView = projectRouteData.canView;

            const allowed = canView
                ? await this.auth.canViewProject(canView)
                : true;
            if (!allowed) continue;

            projectRoute.children.forEach((child) => {
                const data = child.data ?? {};
                result.push({
                    name: (child as any).name || child.path,
                    route: [
                        '/dashboards',
                        projectRoute.path as string,
                        child.path as string,
                    ],
                    data: {
                        navbarGroup: data['navbarGroup'] as string | undefined,
                        disabled: data['disabled'] as boolean | undefined,
                    },
                });
            });
        }

        return result;
    }

    /**
     * Returns a list of dashboards divided into groups.
     *
     * This method uses previously retrieved dashboards and groups
     * them under the name specified in the routing.
     * @returns NavbarGroup[]
     */
    private static getGroupedDashboards(
        dashboards: DashboardRoute[],
        groupOrder: string[]
    ): NavbarGroup[] {
        const map = new Map<string, DashboardRoute[]>();
        dashboards
            .filter((d) => (d.data as NavbarData)?.navbarGroup)
            .forEach((d) => {
                const key = (d.data as NavbarData)?.navbarGroup!;
                if (!map.has(key)) map.set(key, []);
                map.get(key)!.push(d);
            });

        return Array.from(map.entries())
            .sort(([a], [b]) => groupOrder.indexOf(a) - groupOrder.indexOf(b))
            .map(([groupName, items]) => ({
                groupName,
                items,
            }));
    }

    /**
     * Set the navbar submenu title.
     *
     * This method updates navbar submenu title when certain element is salected.
     * When this element is not active anymore, the title is changed to default submenu name.
     */
    private setNavbarSubmenuTitle(): void {
        const active = this.dashboards.find((d) => {
            const tree: UrlTree = this.router.createUrlTree(d.route as any);
            return this.router.isActive(tree, {
                paths: 'subset',
                queryParams: 'subset',
                fragment: 'ignored',
                matrixParams: 'ignored',
            } as IsActiveMatchOptions);
        });

        this.grouped.forEach((g) => (g.selectedTitle = undefined));

        if ((active?.data as NavbarData)?.navbarGroup) {
            const g = this.grouped.find(
                (gr) =>
                    gr.groupName === (active?.data as NavbarData)?.navbarGroup
            );
            if (g) g.selectedTitle = active?.name;
        }
    }

    /**
     * Toggle developer mode.
     *
     * Activate developer mode and refreshes the visible dashboards.
     */
    public toggleDeveloperMode(): void {
        this.showDevDashboards = !this.showDevDashboards;
        this.initDashboards();
    }
}
